import Reveal from '../components/Reveal';
import InquiryForm from '../components/InquiryForm';
import ManufacturerPathways from '../components/ManufacturerPathways';
import { CONTACT } from '../content/site';
import usePageMeta from '../hooks/usePageMeta';

const AUDIENCE = [
  'Product managers',
  'Engineers',
  'Industrial designers',
  'Business development teams',
  'Product safety teams',
  'Procurement teams',
  'Legal Teams',
];

export default function Manufacturers() {
  usePageMeta(
    'Manufacturers & OEM Partnerships | RangeSafe Commercial Solutions',
    'Appliance manufacturers and OEM teams: explore factory installation of RangeSafe Stove Safety Knobs™ or a licensing partnership to integrate the technology into your own knob designs and appliances.'
  );
  return (
    <main className="pt-16">
      <header
        className="relative overflow-hidden border-b border-line"
        data-testid="manufacturers-hero"
      >
        <img src="/assets/hero/options/factory-generic-f.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" width="1600" height="900" />
        <div className="absolute inset-0 bg-gradient-to-t from-base via-base/70 to-base/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-shell px-5 pb-20 pt-24 sm:px-8 lg:pb-24 lg:pt-36">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-accentText">Manufacturers &amp; OEM Partnerships</p>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Integrated safety
            <br />
            starts here
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-dim sm:text-lg">
            RangeSafe Stove Safety Knobs™ are available to manufacturers either through purchasing
            or licensing options.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-shell px-5 py-14 sm:px-8 lg:py-20" data-testid="section-pathways">
        <Reveal>
          <h2 className="max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
            Two partnership pathways
          </h2>
        </Reveal>
        <div className="mt-10">
          <ManufacturerPathways />
        </div>
      </section>

      <section className="border-y border-line bg-panel/40 py-16 lg:py-20" data-testid="section-audience">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Who this conversation is for</p>
            <div className="mt-6 flex flex-col gap-y-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-dim sm:flex-row sm:flex-wrap sm:gap-x-10 sm:gap-y-3 sm:text-xs xl:flex-nowrap xl:justify-between xl:gap-x-4 xl:text-[10px] xl:tracking-[0.08em]">
              {AUDIENCE.map((a) => (
                <span key={a} className="flex items-center gap-3 xl:whitespace-nowrap">
                  <span className="h-1 w-1 bg-accent" aria-hidden="true" />
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="manufacturers-form" className="scroll-mt-24 border-t border-line py-20 sm:py-24 lg:py-28" data-testid="section-manufacturers-form">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
              Start the conversation
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-200" data-testid="manufacturers-form-intro">
              Tell us about your product line and RangeSafe’s Commercial Solutions team will work
              with you to find the best solution - factory installation or technology licensing.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-10 border border-line bg-panel/40 p-6 sm:p-10 lg:p-12" data-testid="manufacturers-form-panel">
              <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
                <div>
                  <InquiryForm type="oem" submitLabel="Submit" manufacturer />
                </div>
                <div className="space-y-8 lg:border-l lg:border-line lg:pl-16">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Direct contact</p>
                    <p className="mt-4 font-mono text-sm text-white">
                      <a href={`mailto:${CONTACT.commercialEmail}`} className="hover:text-accent2" data-testid="manufacturers-email">{CONTACT.commercialEmail}</a>
                    </p>
                    <p className="mt-1 font-mono text-sm text-white">
                      <a href={CONTACT.phoneHref} className="hover:text-accent2" data-testid="manufacturers-phone">{CONTACT.phone}</a>
                    </p>
                  </div>
                  <p className="border-t border-line pt-6 text-xs leading-relaxed text-mute">
                    No confidential details are required at this stage. Your information will be
                    shared solely with the RangeSafe Commercial Solutions Team.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
